import { AsyncPipe } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTabsModule } from '@angular/material/tabs';
import { Title } from '@angular/platform-browser';
import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { translate, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import {
  AuthComponent,
  AuthService,
  BirdzTenantService,
  CardTenantComponent,
  DrawerComponent,
  HeaderComponent,
  IfAuthorizedDirective,
  LayoutComponent,
  LoaderService,
  MessengerService,
  ModalGdprComponent,
  Profile,
  SidebarComponent,
} from '@web/birdz-angular';
import { FilterComponent } from 'app/components/filter/filter.component';
import { Tab } from 'app/interfaces/tab.interface';
import {
  catchError,
  combineLatest,
  filter,
  finalize,
  map,
  Observable,
  of,
  skipWhile,
  startWith,
  switchMap,
  take,
  tap,
  throwError,
} from 'rxjs';
import { User } from '../../models/user.model';
import { UserService } from '../../services/user.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [
    RouterOutlet,
    MatTabsModule,
    DrawerComponent,
    FilterComponent,
    MatMenuModule,
    TranslocoPipe,
    RouterLink,
    RouterLinkActive,
    LayoutComponent,
    HeaderComponent,
    AsyncPipe,
    AuthComponent,
    CardTenantComponent,
    IfAuthorizedDirective,
    SidebarComponent,
    MatIconModule,
  ],
})
export class AppComponent implements OnInit {
  private userService = inject(UserService);
  private loader = inject(LoaderService);
  private auth = inject(AuthService);
  private messenger = inject(MessengerService);
  private title = inject(Title);
  dialog = inject(MatDialog);
  private transloco = inject(TranslocoService);
  private tenantService = inject(BirdzTenantService);
  private router = inject(Router);

  profile$: Observable<Profile>;
  isLoading$: Observable<boolean>;
  settings$: Observable<any>;
  appName$: Observable<string> = combineLatest([
    this.userService.user$,
    this.transloco.selectTranslate('APP_NAME'),
  ]).pipe(map(([user, name]) => (user?.getEmail() === 'justine.chateau@veolia.com' ? 'KériTools' : name)));
  appIcon$ = this.userService.user$.pipe(map((user) => user?.getApplication('querytools')?.logo ?? ''));
  logoRedirectionUrl = '';
  needsTenant$: Observable<boolean>;
  isOnQueryRoute = toSignal(
    this.router.events.pipe(
      filter((e) => e instanceof NavigationEnd),
      map((e) => (e as NavigationEnd).urlAfterRedirects.startsWith('/query')),
      startWith(false)
    ),
    { initialValue: false }
  );
  isDev = toSignal(this.userService.user$.pipe(map((u) => u?.isDev() ?? false)), { initialValue: false });
  tabs: Tab[] = [
    {
      label: `TAB_FAVORITES`,
      path: `favorites`,
      icon: 'star',
    },
    {
      label: `TAB_QUERIES`,
      path: `queries`,
      icon: 'list',
    },
    {
      label: `TAB_MY_QUERY`,
      path: `query`,
      icon: 'sql',
    },
    {
      label: `TAB_BIGQUERY_USAGE`,
      path: `bigquery-usage`,
      icon: 'attach_money',
      developerOnly: true,
    },
  ];

  constructor() {
    this.profile$ = this.userService.user$.pipe(map((user) => user.toProfile()));
    this.settings$ = this.userService.user$.pipe(map((user: User) => user?.['settings']));
    this.isLoading$ = this.loader.isLoading$;
    this.needsTenant$ = combineLatest([
      this.userService.user$,
      this.tenantService.tenantId$.pipe(startWith(null)),
    ]).pipe(map(([user, tenantId]) => user?.isBirdzAdmin() === true && !tenantId));
  }

  ngOnInit() {
    this.setLogo();
    this.userService.fetchUser().subscribe();
    this.translateTitle();
    this.handleGDPR();
  }

  handleGDPR() {
    this.userService.needConsent$
      .pipe(
        skipWhile((needConsent) => !needConsent),
        take(1)
      )
      .subscribe(() => {
        this.openGDPRModal(true);
      });
  }

  openGDPRModal(requireConsent = false) {
    const config = {
      data: {
        appName$: this.appName$,
        submit$: requireConsent ? this.userService.grantConsent() : null,
      },
    };

    this.dialog
      .open(ModalGdprComponent, config)
      .afterClosed()
      .subscribe(() => {
        if (requireConsent) {
          this.userService.setNeedConsent(false);
        }
      });
  }

  translateTitle() {
    this.appName$.pipe(tap((name) => this.title.setTitle(name))).subscribe();
  }

  logout() {
    this.userService
      .clearCache()
      .pipe(
        finalize(() =>
          this.auth
            .logout()
            .pipe(finalize(() => this.auth.redirectToLogin()))
            .subscribe()
        )
      )
      .subscribe();
  }

  onUserSettingsChange(settings: Record<string, any>) {
    this.userService
      .patchSettings(settings)
      .pipe(
        this.messenger.showFriendlyErrorMessage(),
        switchMap(() => this.transloco.selectTranslate('USER_SETTINGS_UPDATED')),
        tap((message) => this.messenger.success(message))
      )
      .subscribe();
  }

  contactCallback = (form: FormData) => {
    const extra = {};

    form.append('url', window.location.href);
    form.append('extra', JSON.stringify(extra));

    return this.userService.contactSupport(form).pipe(
      catchError((error) => {
        return throwError(error.friendlyMessage);
      })
    );
  };

  setLogo() {
    this.userService.user$.pipe(tap((user) => this.setLogoUrl(user))).subscribe();
  }

  setLogoUrl(user: User | null) {
    const portalApplication = user?.getApplication('portal');
    if (portalApplication) {
      this.logoRedirectionUrl = portalApplication.url;
    }
  }

  onAvatarFileChanged(file: File | null): void {
    if (!file) {
      return;
    }

    this.userService
      .updateUserAvatar(file)
      .pipe(
        tap((profile: Profile) => {
          this.profile$ = of(profile);
          this.messenger.success(translate('birdz.avatar_upload_success'));
        }),
        this.messenger.showFriendlyErrorMessage()
      )
      .subscribe();
  }
}
